import React, {useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {KTSVG} from '../../../template/helpers'
import {cdrAPI} from '../../../services/CDRService'
import CreatableSelect from 'react-select/creatable'
import {Button, Overlay, Popover} from 'react-bootstrap'
import Select, {SingleValue, MultiValue} from 'react-select'
import {updateQuery, setQuery} from '../slices/erFilterSlice'
import {columnMetaEr, dataTableConfig} from '../config/data-table'
import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {FilterRule, FilterKeyName, ColumnMeta} from '../core/_models'
import {Option} from '../../types/models'
import {useAccount} from '../../../hooks/useAccount'
import {
  agentEmployeeOperators,
  isAgentEmployeeFilter,
  sanitizeAgentEmployeeRule,
} from '../../cdr/core/agentEmployeeFilter'

const genId = () => Math.random().toString(36).slice(2)

export function ErFilter() {
  const {appId} = useParams()
  const dispatch = useAppDispatch()
  const [show, setShow] = useState(false)
  const target = useRef<HTMLButtonElement | null>(null)
  const [filters, setFilters] = useState<FilterRule[]>([])
  const {data: cdrLines} = cdrAPI.useGetLinesQuery(appId!)
  const {data: cdrUsers} = cdrAPI.useGetUsersQuery(appId!)
  const {isAgent} = useAccount()
  const query = useAppSelector((state) => state.erFilter)
  const employeeOptions = cdrUsers?.users.map((u) => u.name || u.number) ?? []

  useEffect(() => {
    if (query?.rules?.items.length) {
      const savedFilters = query.rules.items
        .map((item) => {
          const meta = columnMetaEr.find((c) => c.key === item.column)
          if (!meta) {
            return null
          }

          const ops = dataTableConfig[meta.type]
          if (!ops) {
            return null
          }
          const opItem = ops.find((o) => o.value === item.operator)
          if (!opItem) {
            return null
          }
          const rule = {
            id: item.id,
            column: item.column,
            type: meta.type,
            operator: opItem.value,
            value: item.value,
          } as FilterRule
          return isAgentEmployeeFilter(isAgent, rule.column) && cdrUsers
            ? sanitizeAgentEmployeeRule(rule, employeeOptions)
            : rule
        })
        .filter(Boolean) as FilterRule[]
      if (savedFilters.length) {
        setFilters((prev) => [...savedFilters, ...prev.filter((p) => !savedFilters.some((s) => s.column === p.column))])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isAgent, cdrUsers])

  useEffect(() => {
    if (query) {
      const toRule = query.rules?.items?.find((rule) => rule.column === 'to_')
      const didRule = query.rules?.items?.find((rule) => rule.column === 'did_number')
      const directionRule = query.rules?.items?.find((rule) => rule.column === 'direction')

      if (toRule === undefined) {
        addFilter('to_')
      }
      if (didRule === undefined) {
        addFilter('did_number')
      }
      if (directionRule === undefined) {
        dispatch(
          setQuery({
            ...query,
            rules: {
              operator: '$and',
              items: [
                {
                  id: genId(),
                  column: 'direction',
                  type: 'string',
                  operator: 'eq',
                  value: 'ALL',
                },
              ],
            },
          })
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addFilter = (column: FilterKeyName) => {
    const meta = columnMetaEr.find((c) => c.key === column)
    if (!meta) return

    const firstOperator = dataTableConfig[meta.type][0]

    setFilters((p) => [
      ...p,
      {
        id: genId(),
        column: meta.key,
        type: meta.type,
        operator: firstOperator.value,
        value: meta.type === 'multi-select' ? [] : '',
      },
    ])
  }

  const updateFilter = (id: string, patch: Partial<FilterRule>) => {
    setFilters((p) => p.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }

  const removeAllFilters = () => {
    setFilters([])
    addFilter('to_')
    addFilter('did_number')
    const directionRule = query?.rules?.items?.find((rule) => rule.column === 'direction')
    dispatch(
      setQuery({
        ...query,
        rules: {
          operator: '$and',
          items: [directionRule!],
        },
      })
    )

    // dispatch(resetQuery())
    setShow(false)
  }

  const isMultiValueOperator = (op: string) => op === 'inArray' || op === 'notInArray'

  const applyFilters = () => {
    const rulesPayload = filters.map(
      ({id, column, operator, value}) =>
        ({
          id,
          column,
          operator: operator,
          value,
        } as FilterRule)
    )

    const directionRule = query?.rules?.items?.find((rule) => rule.column === 'direction')

    dispatch(
      updateQuery({
        ...query,
        rules: {
          operator: '$and',
          items: [...rulesPayload, directionRule!],
        },
      })
    )
    setShow(false)
  }

  const optionHandler = (meta: ColumnMeta): {name: string}[] => {
    if (meta.key === 'to_') {
      return employeeOptions.map((name) => ({name}))
    }
    if (meta.key === 'did_number') {
      return cdrLines?.lines ?? []
    }

    return []
  }

  const count = filters.some((rule) => {
    if (Array.isArray(rule.value)) {
      return rule.value.length > 0
    }
    return rule.value !== ''
  })
    ? 1
    : 0

  return (
    <>
      <Button
        ref={target}
        size='sm'
        className='btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary'
        onClick={() => setShow((v) => !v)}
      >
        <div className='flex align-content-center'>
          <i className='bi bi-filter fs-5' />
          Сотрудники и линии
          {count > 0 && (
            <KTSVG path='/media/icons/duotune/abstract/abs050.svg' className='text-success svg-icon-5 ms-1' />
          )}
        </div>
      </Button>

      <Overlay target={target.current} show={show} placement='bottom-start' rootClose onHide={() => setShow(false)}>
        <Popover style={{minWidth: 900, maxWidth: 1200}}>
          <Popover.Header as='div' className='d-flex align-items-center py-1 justify-content-between'>
            Сотрудники и линии
            <button className='btn btn-sm btn-icon btn-active-color-primary' onClick={() => setShow(false)}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </button>
          </Popover.Header>

          <Popover.Body>
            {filters.map((f, i) => {
              const meta = columnMetaEr.find((c) => c.key === f.column)!
              const multiValue = Array.isArray(f.value) ? f.value : []
              const agentEmployee = isAgentEmployeeFilter(isAgent, f.column)
              const operators = agentEmployee
                ? agentEmployeeOperators(dataTableConfig[f.type])
                : dataTableConfig[f.type]

              return (
                <div key={f.id} className='align-items-center' style={{display: 'flex', gap: 6, marginBottom: 6}}>
                  {i === 0 ? (
                    <span className='badge badge-light-primary min-w-50px justify-content-center'>где</span>
                  ) : (
                    <span className='min-w-50px' />
                  )}
                  <Select
                    className='react-select-styled react-select-solid min-w-200px'
                    classNamePrefix='react-select'
                    options={[]}
                    menuIsOpen={false}
                    value={{label: meta.label, value: meta.key}}
                    isDisabled={true}
                    styles={{
                      indicatorsContainer: () => ({display: 'none'}),
                      indicatorSeparator: () => ({display: 'none'}),
                    }}
                  />
                  <Select
                    className='react-select-styled react-select-solid min-w-150px'
                    classNamePrefix='react-select'
                    options={operators}
                    value={operators.find((o) => o.value === f.operator)}
                    onChange={(v: SingleValue<Option<string>>) => v && updateFilter(f.id, {operator: v.value})}
                  />
                  <div style={{flex: 1}}>
                    {f.type === 'string' && !isMultiValueOperator(f.operator) && (
                      <input
                        id={f.id}
                        className='form-control'
                        value={f.value as string}
                        placeholder='Введите значение'
                        onChange={(e) => updateFilter(f.id, {value: e.target.value})}
                      />
                    )}

                    {isMultiValueOperator(f.operator) && f.type !== 'multi-select' && (
                      <CreatableSelect<Option<string>, true>
                        key={f.id}
                        isMulti
                        className='react-select-multi-custom react-select-solid'
                        classNamePrefix='react-select'
                        options={optionHandler(meta).map((o: any) => ({label: o.name, value: o.name}))}
                        isValidNewOption={agentEmployee ? () => false : undefined}
                        placeholder={agentEmployee ? 'Выберите сотрудника' : 'Введите значение и нажмите Enter или Tab'}
                        value={multiValue.map((v) => ({
                          label: v,
                          value: v,
                        }))}
                        onChange={(v: MultiValue<Option<string>>) => updateFilter(f.id, {value: v.map((o) => o.value)})}
                      />
                    )}
                  </div>
                </div>
              )
            })}

            <div className='d-flex align-items-center mt-4 gap-2'>
              <Button
                size='sm'
                className='w-100 btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary'
                onClick={removeAllFilters}
                disabled={filters.length < 1}
              >
                Сбросить
              </Button>
              <Button size='sm' className='w-100' onClick={applyFilters} disabled={filters.length < 1}>
                Применить
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}
